import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Zap, Play, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const ICONS = {
  sla_nudge: Clock,
  stage_change_task: Zap,
  weekly_digest: CheckCircle2,
  welcome_won: CheckCircle2,
  renewal_reminder: Clock,
};

export default function Automations() {
  const [rules, setRules] = useState([]);
  const [running, setRunning] = useState(null);

  const load = async () => setRules((await api.get("/automations")).data);
  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    await api.patch(`/automations/${key}/toggle`, { enabled });
    toast.success(enabled ? "Enabled" : "Disabled");
    load();
  };

  const run = async (key) => {
    setRunning(key);
    try {
      const r = await api.post(`/automations/${key}/run`);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error("Run failed"); }
    finally { setRunning(null); }
  };

  return (
    <AppLayout title="Automations">
      <div className="miro-card p-4 mb-5 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#0EA5FF18", color: "#0284C7" }}>
          <Zap size={17}/>
        </div>
        <div className="text-sm text-[var(--miro-text-secondary)]">
          Toggle rules on to activate them and hit <span className="font-semibold text-[var(--miro-navy)]">Run now</span> to execute manually.
          In production these run on a scheduler. Every action is auditable.
        </div>
      </div>

      <div className="space-y-3">
        {rules.map(r => {
          const Icon = ICONS[r.key] || Zap;
          return (
            <div key={r.key} data-testid={`rule-${r.key}`} className="miro-card p-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: r.enabled ? "#5FBF3A18" : "#F1F5F9", color: r.enabled ? "#15803D" : "#64748B" }}>
                  <Icon size={19}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-[var(--miro-navy)]">{r.name}</h3>
                    <Badge className="border-0 font-mono text-[10px]" style={{ background: "#F1F5F9", color: "#475569" }}>{r.key}</Badge>
                    {r.enabled && <Badge className="border-0 font-semibold" style={{ background: "#DCFCE7", color: "#15803D" }}>Enabled</Badge>}
                  </div>
                  <p className="text-sm text-[var(--miro-text-secondary)] mt-1">{r.description}</p>
                  {r.last_run_at && (
                    <div className="text-[11px] text-[var(--miro-text-muted)] mt-2">
                      Last run {formatDistanceToNow(new Date(r.last_run_at), { addSuffix: true })} ·
                      <span className="text-[var(--miro-navy)] font-semibold"> {r.last_run_affected} affected</span> · {r.last_run_message}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid={`rule-toggle-${r.key}`}
                    checked={!!r.enabled}
                    onCheckedChange={(v) => toggle(r.key, v)}
                  />
                  <Button
                    data-testid={`rule-run-${r.key}`}
                    onClick={() => run(r.key)}
                    disabled={running === r.key}
                    size="sm" variant="outline"
                  >
                    <Play size={14} className="mr-1"/>
                    {running === r.key ? "Running…" : "Run now"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AppLayout>
  );
}
